import { subscribeUserToPush } from '@helpers/subscribeUserToPush';
import { useInitContext } from '@providers/InitProvider';
import { useAPI } from '@providers/useAPI';
import { AuthService, LastNotificationResponseDto, MeResponseDto } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import { getRefreshPromise } from 'app/http';
import { invalidateSession } from 'app/http/sessionInvalidation';
import { authKeys } from 'app/queries/auth/authKeys';
import { createFetchAuthMeQueryFn } from 'app/queries/auth/createFetchAuthMeQueryFn';

import { useQueryClient } from '@tanstack/react-query';
import { EPermission } from 'app/types/enums/EPermission';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PermissionProvider } from './PermissionProvider/PermissionProvider';

declare global {
  interface Window {
    /** Hotjar SDK; opts shape is not typed in our codebase. */
    hj: (apiCall: string, id: string, opts: Record<string, string | number | boolean>) => void;
  }
}

type TProps = {
  children: ReactNode;
};

type TOrganization = NonNullable<MeResponseDto['organizations']>[number];
type TWorkspace = NonNullable<TOrganization['workspaces']>[number];
type TUpdateMeOptions = {
  includeNotifications?: boolean;
};
type TUpdateMeResult = MeResponseDto | undefined;

type TState = {
  me: MeResponseDto;
  lastNotifications: LastNotificationResponseDto[];
  isLoggedIn: boolean;
  isOrgResolved: boolean;
  updateMe: (options?: TUpdateMeOptions) => Promise<TUpdateMeResult>;
  updateLastNotifications: () => Promise<void>;
  clearMe: () => void;
  workspace: TWorkspace | undefined;
  setWorkspace: (arg: TWorkspace | undefined) => void;
  organization: TOrganization | undefined;
  setMe: (arg: MeResponseDto) => void;
  notificationsReceiveDate: number;
  removeWorkspace: (workspaceId: string) => void;
};

const parsePermissions =
  (me: MeResponseDto, organization: TOrganization | undefined) =>
  async (permission: EPermission): Promise<boolean> => {
    const org = me.organizations?.find((org) => org.id === organization?.id);

    if (!org) {
      return false;
    }

    return !!org.permissions[permission];
  };

const AuthContext = createContext<TState>({
  me: {} as MeResponseDto,
  lastNotifications: [],
  isLoggedIn: false,
  isOrgResolved: false,
  updateMe: async () => undefined,
  updateLastNotifications: async () => {},
  clearMe: () => {},
  workspace: undefined,
  setWorkspace: () => {},
  organization: undefined,
  setMe: () => {},
  notificationsReceiveDate: 0,
  removeWorkspace: () => {},
});

const shouldUpdateOrganization = (prev: TOrganization | undefined, next: TOrganization | undefined) => {
  if (!next) {
    return false;
  }

  if (!prev) {
    return true;
  }

  return (
    prev.id !== next.id ||
    prev.name !== next.name ||
    prev.slug !== next.slug ||
    prev.image !== next.image ||
    prev.deleted !== next.deleted ||
    prev.asOneAllowed !== next.asOneAllowed ||
    JSON.stringify(prev.featureFlags ?? {}) !== JSON.stringify(next.featureFlags ?? {}) ||
    prev.billingAccount?.id !== next.billingAccount?.id ||
    prev.billingAccount?.FreeCreditBalance?.currentBalance !== next.billingAccount?.FreeCreditBalance?.currentBalance ||
    prev.billingAccount?.MainBalance?.currentBalance !== next.billingAccount?.MainBalance?.currentBalance ||
    prev.workspaces?.length !== next.workspaces?.length
  );
};

const shouldUpdateWorkspace = (prev: TWorkspace | undefined, next: TWorkspace | undefined) => {
  if (!next) {
    return false;
  }

  if (!prev) {
    return true;
  }

  return (
    prev.id !== next.id ||
    prev.slug !== next.slug ||
    prev.name !== next.name ||
    prev.organizationId !== next.organizationId
  );
};

const saveSelectedOrganizationSlug = (slug: string) => {
  window.sessionStorage.setItem('selectedOrganizationSlug', slug);
};

export const AuthProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const { isAuthenticated } = useInitContext();
  const location = useLocation();
  const queryClient = useQueryClient();

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const includeNotificationsForNextMeFetchRef = useRef(false);
  const standaloneNotificationAbortControllersRef = useRef(new Set<AbortController>());

  const [me, setMe] = useState<MeResponseDto>({} as MeResponseDto);
  const [lastNotifications, setLastNotifications] = useState<LastNotificationResponseDto[]>([]);
  const [notificationsReceiveDate, setNotificationsReceiveDate] = useState<number>(0);
  const [workspace, setWorkspace] = useState<TWorkspace>();
  const [organization, setOrganization] = useState<TOrganization>();
  const [isOrgResolved, setIsOrgResolved] = useState(false);

  const isLoggedIn = !!me?.id;

  const fetchAndApplyLastNotifications = useCallback(
    async (signal: AbortSignal) => {
      try {
        const data = await call(() => cancelOnSignal(AuthService.lastNotifications(), signal));
        if (!signal.aborted && data !== undefined) {
          setLastNotifications(data);
          setNotificationsReceiveDate(Date.now());
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('Unable to update notifications', error);
        }
      }
    },
    [call],
  );

  const updateLastNotifications = useCallback(async () => {
    const abortController = new AbortController();
    standaloneNotificationAbortControllersRef.current.add(abortController);
    try {
      await fetchAndApplyLastNotifications(abortController.signal);
    } finally {
      standaloneNotificationAbortControllersRef.current.delete(abortController);
    }
  }, [fetchAndApplyLastNotifications]);

  const applyMe = useCallback((data: MeResponseDto): TUpdateMeResult => {
    if (!data?.id) {
      console.error('Unable to retrieve user account (logged out)');
      return undefined;
    }

    if (!data?.organizations) {
      throw new Error('Unable to retrieve user organization details');
    }

    if (window.hj) {
      window.hj('identify', data.id, { email: data.email, name: `${data.name} ${data.lastname}` });
    }

    setMe({ ...data, organizations: [...data.organizations] });

    setOrganization((currentOrg) => {
      const newOrg = data.organizations.find((org) => org.id === currentOrg?.id);
      if (newOrg) {
        const organizationChanged = shouldUpdateOrganization(currentOrg, newOrg);
        if (organizationChanged) {
          saveSelectedOrganizationSlug(newOrg.slug);
          return newOrg;
        }
      }
      return currentOrg;
    });

    setWorkspace((currentWs) => {
      if (!currentWs) {
        return currentWs;
      }
      const newOrg = data.organizations.find((org) => org.id === currentWs.organizationId);
      if (!newOrg) {
        return currentWs;
      }
      const newWs = newOrg.workspaces?.find((ws) => ws.id === currentWs.id);
      if (newWs) {
        if (shouldUpdateWorkspace(currentWs, newWs)) {
          return newWs;
        }
        return currentWs;
      }
      if (!newOrg.workspaces?.length) {
        return undefined;
      }
      return currentWs;
    });

    if (data.name && !data.organizations.length && data.invitations?.length) {
      console.log('No orgs but invitations are present!');
    }
    return data;
  }, []);

  const fetchAuthMeQueryFn = useMemo(() => createFetchAuthMeQueryFn(call), [call]);

  const fetchMeQueryFn = useCallback(
    async (context: Parameters<typeof fetchAuthMeQueryFn>[0]) => {
      const includeNotifications = includeNotificationsForNextMeFetchRef.current;
      includeNotificationsForNextMeFetchRef.current = false;

      if (includeNotifications) {
        await fetchAndApplyLastNotifications(context.signal);
        if (context.signal.aborted) {
          throw new DOMException('The operation was aborted', 'AbortError');
        }
      }

      try {
        return await fetchAuthMeQueryFn(context);
      } catch (error) {
        console.error('Unable to update user account', error);
        throw error;
      }
    },
    [fetchAndApplyLastNotifications, fetchAuthMeQueryFn],
  );

  const updateMe = useCallback(
    async (options: TUpdateMeOptions = {}): Promise<TUpdateMeResult> => {
      const currentQuery = queryClient.getQueryCache().find({ exact: true, queryKey: authKeys.me() });
      const joinsActiveFetch = currentQuery?.state.fetchStatus === 'fetching';
      includeNotificationsForNextMeFetchRef.current ||= options.includeNotifications ?? true;

      const invalidationPromise = joinsActiveFetch
        ? queryClient.invalidateQueries({ queryKey: authKeys.me(), refetchType: 'none' })
        : Promise.resolve();
      const fetchPromise = queryClient.fetchQuery({
        queryKey: authKeys.me(),
        queryFn: fetchMeQueryFn,
        structuralSharing: false,
      });
      const queryForFetch = queryClient.getQueryCache().find({ exact: true, queryKey: authKeys.me() });

      let data: MeResponseDto | undefined;
      try {
        data = await fetchPromise;
      } catch {
        data = undefined;
      }

      if (
        !queryForFetch ||
        queryClient.getQueryCache().find({ exact: true, queryKey: authKeys.me() }) !== queryForFetch
      ) {
        return undefined;
      }

      if (!joinsActiveFetch) {
        if (!data) {
          return undefined;
        }
        try {
          return applyMe(data);
        } catch (error) {
          console.error('Unable to update user account', error);
          return undefined;
        }
      }

      await invalidationPromise;
      if (queryClient.getQueryCache().find({ exact: true, queryKey: authKeys.me() }) !== queryForFetch) {
        return undefined;
      }

      try {
        await queryClient.invalidateQueries(
          { queryKey: authKeys.me(), refetchType: 'all' },
          { cancelRefetch: false, throwOnError: true },
        );
      } catch {
        return undefined;
      }

      if (queryClient.getQueryCache().find({ exact: true, queryKey: authKeys.me() }) !== queryForFetch) {
        return undefined;
      }

      const trailingData = queryClient.getQueryData<MeResponseDto>(authKeys.me());
      if (!trailingData) {
        return undefined;
      }

      try {
        return applyMe(trailingData);
      } catch (error) {
        console.error('Unable to update user account', error);
        return undefined;
      }
    },
    [applyMe, fetchMeQueryFn, queryClient],
  );

  const removeWorkspace = useCallback(
    (workspaceId: string) => {
      setMe((prev) => {
        if (!prev?.id) {
          return prev;
        }

        const updatedOrgs = prev.organizations.map((org) => ({
          ...org,
          workspaces: org.workspaces.filter((ws) => ws.id !== workspaceId),
        }));

        return { ...prev, organizations: updatedOrgs };
      });

      // If the deleted workspace is the current one, switch to another
      if (workspace?.id === workspaceId) {
        if (organization?.workspaces) {
          const remainingWorkspaces = organization.workspaces.filter((ws) => ws.id !== workspaceId);
          if (remainingWorkspaces.length > 0) {
            setWorkspace(remainingWorkspaces[0]);
          } else {
            setWorkspace(undefined);
          }
        }
      }

      // Update the current organization to reflect the removed workspace
      if (organization?.workspaces?.some((ws) => ws.id === workspaceId)) {
        saveSelectedOrganizationSlug(organization.slug);
        setOrganization({
          ...organization,
          workspaces: organization.workspaces.filter((ws) => ws.id !== workspaceId),
        });
      }

      setNotificationsReceiveDate(Date.now());
    },
    [organization, workspace],
  );

  useEffect(() => {
    if (isAuthenticated) {
      void updateMe();
    }
  }, [isAuthenticated, updateMe]);

  const clearMe = useCallback(() => {
    includeNotificationsForNextMeFetchRef.current = false;
    standaloneNotificationAbortControllersRef.current.forEach((abortController) => abortController.abort());
    standaloneNotificationAbortControllersRef.current.clear();
    queryClient.removeQueries({ queryKey: authKeys.all });
    setMe({} as MeResponseDto);
    setOrganization(undefined);
    setWorkspace(undefined);
    setIsOrgResolved(false);
  }, [queryClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearMe();
    }
  }, [clearMe, isAuthenticated]);

  useEffect(() => {
    if (!me?.id) {
      return;
    }

    subscribeUserToPush(updateLastNotifications).catch((error) =>
      console.error('Error subscribing to push notifications:', error),
    );
  }, [me?.id]);

  // Track previous pathname to detect URL changes
  const prevPathnameRef = useRef<string>(location.pathname);

  useEffect(() => {
    if (!me?.id) {
      setIsOrgResolved(false);
      return;
    }

    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    const organizationInSlug = getOrgFromSlug();

    if (pathnameChanged && organizationInSlug && organizationInSlug.id !== organization?.id) {
      setIsOrgResolved(false);
    }

    // Only update organization from slug when URL actually changes
    // This prevents switching when me is updated via WebSocket
    if (pathnameChanged || !organization) {
      updateOrgFromSlug();
    }

    if (!me.organizations?.length) {
      setIsOrgResolved(true);
      return;
    }

    if (organization) {
      setIsOrgResolved(true);
    }
  }, [me, location.pathname, organization]);

  useEffect(() => {
    updateWSFromSlug();
  }, [organization?.id, location.pathname]);

  /**
   * Get initial org from slug (if present)
   * If not present and organization is not set then set first organization
   * If organization is the same, skip organization update to prevent reloading
   * IMPORTANT: This function is called when URL changes, not when data is updated via WebSocket
   */
  const updateOrgFromSlug = () => {
    if (!me?.organizations || !me?.organizations.length) {
      return;
    }
    const organizationInSlug = getOrgFromSlug();

    // If no organization in URL and no current organization, set first one
    if (!organizationInSlug && !organization) {
      setOrganization(me.organizations[0]);
      return;
    }

    // If no organization in URL but we have a current one, don't switch
    if (!organizationInSlug) {
      return;
    }

    // Organization found in URL
    if (organization) {
      if (organizationInSlug?.id === organization.id) {
        if (shouldUpdateOrganization(organization, organizationInSlug)) {
          setOrganization(organizationInSlug);
          saveSelectedOrganizationSlug(organizationInSlug.slug);
        }
        return;
      }

      // Different organization in URL - switch to it
      // This only happens when URL explicitly changes
      setOrganization(organizationInSlug);
      saveSelectedOrganizationSlug(organizationInSlug.slug);
      return;
    }

    // No current organization, but found one in URL - set it
    setOrganization(organizationInSlug);
    saveSelectedOrganizationSlug(organizationInSlug.slug);
  };

  const getOrgFromSlug = () => {
    if (!me?.organizations || !me?.organizations.length) {
      return;
    }

    const pathname = location.pathname;

    // First, try to find organization by exact match in URL path
    // This is the most reliable way - look for /orgSlug/ at the start of pathname
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0];
      const orgFromPath = me.organizations.find((o) => o.slug === firstSegment);
      if (orgFromPath) {
        return orgFromPath;
      }
    }

    const selectedOrganizationSlug = window.sessionStorage.getItem('selectedOrganizationSlug');
    if (selectedOrganizationSlug) {
      return me.organizations.find((o) => o.slug === selectedOrganizationSlug);
    }

    return undefined;
  };

  const updateWSFromSlug = () => {
    if (!organization || !organization.workspaces?.length) {
      return;
    }
    const workspaceInSlug = getWSFromSlug();

    // If workspace found in URL, always use it
    if (workspaceInSlug) {
      // Always set workspace from URL if it's found, regardless of current workspace
      // This ensures that on page refresh, the correct workspace from URL is used
      if (!workspace || workspace.id !== workspaceInSlug.id) {
        setWorkspace(workspaceInSlug);
        return;
      }
      // Same workspace, update data if properties changed (e.g., slug was updated)
      if (
        workspace.slug !== workspaceInSlug.slug ||
        workspace.name !== workspaceInSlug.name ||
        workspace.organizationId !== workspaceInSlug.organizationId
      ) {
        setWorkspace(workspaceInSlug);
      }
      return;
    }

    // No workspace found in URL
    // Only set first workspace if we don't have a current workspace
    // or if current workspace is not in this organization
    if (!workspace || workspace.organizationId !== organization.id) {
      if (organization.workspaces.length > 0) {
        setWorkspace(organization.workspaces[0]);
      }
      return;
    }
    // Otherwise, keep current workspace - don't switch automatically
  };

  const getWSFromSlug = () => {
    if (!organization || !organization.workspaces || !organization.workspaces.length) {
      return;
    }

    const pathname = location.pathname;
    const orgSlug = organization.slug;

    // Match patterns like:
    // /org/workspaces/ws/jobs
    // /org/workspaces/ws/jobs/details/123
    // /org/workspaces/ws/details
    // etc.
    const workspacePrefix = `/${orgSlug}/workspaces/`;

    // Check if pathname starts with the workspace prefix
    if (!pathname.startsWith(workspacePrefix)) {
      return;
    }

    // Extract the part after /org/workspaces/
    const afterPrefix = pathname.slice(workspacePrefix.length);
    if (!afterPrefix) {
      return;
    }

    // Extract workspace slug (everything before the next /)
    const workspaceSlug = afterPrefix.split('/')[0];
    if (!workspaceSlug) {
      return;
    }

    // Find workspace by slug
    return organization.workspaces.find((w) => w.slug === workspaceSlug && w.organizationId === organization.id);
  };

  useEffect(() => {
    if (!me?.id && refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      return;
    }

    if (me?.id && !refreshIntervalRef.current) {
      refreshIntervalRef.current = setInterval(() => {
        void getRefreshPromise()
          .then((result) => {
            if (result.kind === 'auth-failure') {
              invalidateSession({ broadcast: true });
            }
          })
          .catch((error) => {
            console.error('Unable to refresh authentication', error);
          });
      }, 200000);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [me?.id]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isOrgResolved,
        me,
        lastNotifications,
        updateMe,
        updateLastNotifications,
        clearMe,
        workspace,
        setWorkspace,
        organization,
        setMe,
        notificationsReceiveDate,
        removeWorkspace,
      }}
    >
      <PermissionProvider fetchPermission={parsePermissions(me, organization)}>{children}</PermissionProvider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
